import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import companyService from '../services/companyService';
import { adminService } from '../services/adminService';
import { useModal } from '../context/ModalContext';
import { copyTextToClipboard } from '../utils/clipboard.js';

/**
 * Platform console. Only the platform owner reaches this page.
 *
 * Everything a company needs to become usable is created here in one step: the
 * company row and its first admin. A company with no admin cannot be reached by
 * anyone except the platform owner, so the two are provisioned together.
 */

const styles = {
  page: { maxWidth: 1180, margin: '0 auto' },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  title: { margin: 0, fontSize: 22, fontWeight: 650, color: '#111827', letterSpacing: '-0.01em' },
  sub: { margin: '4px 0 0', fontSize: 13, color: '#6b7280' },
  primaryBtn: {
    padding: '9px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#273947',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  ghostBtn: {
    padding: '6px 12px',
    borderRadius: 7,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '6px 12px',
    borderRadius: 7,
    border: '1px solid #fca5a5',
    background: '#fff',
    color: '#b91c1c',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  metric: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '16px 18px',
  },
  metricValue: { fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1.15 },
  metricLabel: { marginTop: 5, fontSize: 11.5, fontWeight: 650, color: '#6b7280' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap',
  },
  directory: { marginTop: 4 },
  directoryHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14,
    marginBottom: 14, flexWrap: 'wrap',
  },
  directoryTitle: { margin: 0, color: '#111827', fontSize: 15, fontWeight: 700 },
  directoryHint: { margin: '3px 0 0', color: '#6b7280', fontSize: 12 },
  companyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(390px, 1fr))',
    gap: 14,
  },
  companyCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
    padding: 18, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
  },
  companyTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  companyIdentity: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  companyAvatar: {
    width: 42, height: 42, borderRadius: 11, display: 'grid', placeItems: 'center',
    color: '#fff', fontSize: 14, fontWeight: 750, flexShrink: 0,
  },
  companyName: { color: '#111827', fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  companyMeta: { marginTop: 4, color: '#6b7280', fontSize: 11.5 },
  domainLink: { color: '#0f766e', textDecoration: 'none', fontSize: 12, fontWeight: 600 },
  companyStats: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
    margin: '17px 0', padding: '13px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
  },
  companyStat: { textAlign: 'center', borderRight: '1px solid #f1f5f9' },
  companyStatValue: { color: '#111827', fontSize: 18, lineHeight: 1.2, fontWeight: 700 },
  companyStatLabel: { color: '#94a3b8', fontSize: 10.5, fontWeight: 650, marginTop: 4 },
  companyFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  openBtn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid #273947', background: '#273947',
    color: '#fff', fontSize: 12.5, fontWeight: 650, cursor: 'pointer',
  },
  secondaryActions: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  smallBtn: {
    padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
    color: '#475569', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  },
  suspendActionBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 9px', border: '1px solid #fed7aa', borderRadius: 8,
    background: '#fffaf5', color: '#c2410c', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  },
  reactivateBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 9px', border: '1px solid #bbf7d0', borderRadius: 8,
    background: '#f0fdf4', color: '#15803d', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  },
  deleteBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 9px', border: '1px solid #fecaca', borderRadius: 8,
    background: '#fff7f7', color: '#b91c1c', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  },
  search: {
    width: 300,
    maxWidth: '100%',
    padding: '8px 11px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 13,
    fontFamily: 'inherit',
  },
  select: {
    padding: '8px 28px 8px 10px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '11px 16px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#6b7280',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '13px 16px',
    fontSize: 13.5,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle',
  },
  pill: (bg, fg) => ({
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background: bg,
    color: fg,
    whiteSpace: 'nowrap',
  }),
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151' },
  input: {
    padding: '9px 11px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 13.5,
    fontFamily: 'inherit',
    color: '#111827',
    background: '#fff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14,
  },
  hint: { fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 14,
  },
};

const emptyForm = {
  companyName: '',
  code: '',
  color: '#273947',
  adminUsername: '',
  adminPassword: '',
  adminDisplayName: '',
  adminEmail: '',
  notes: '',
};

export default function CompaniesPage() {
  const { showAlert, showConfirm, showPrompt } = useModal();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [deletingCompanyId, setDeletingCompanyId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [list, counters, submissionData] = await Promise.all([
        companyService.getAll(),
        companyService.getUsage().catch(() => []),
        adminService.getAllSubmissions().catch(() => ({ submissions: [] })),
      ]);
      const companyList = list || [];
      setCompanies(companyList);
      const map = {};
      (counters || []).forEach((c) => { map[c.companyId] = c; });

      // The usage endpoint includes soft-deleted historical submissions. The
      // support console must count what the owner can actually open, otherwise
      // a company card can say "4" while its workspace correctly shows none.
      companyList.forEach((company) => {
        map[company.id] = { ...(map[company.id] || {}), submissions: 0 };
      });
      const activeSubmissions = Array.isArray(submissionData?.submissions) ? submissionData.submissions : [];
      activeSubmissions.forEach((item) => {
        const owner = companyList.find((company) => item.clinicId === company.id
          || (!item.clinicId && item.clinicCode && item.clinicCode === company.code)
          || (!item.clinicId && item.clinicName && item.clinicName === company.name));
        if (owner) map[owner.id].submissions += 1;
      });
      setUsage(map);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load companies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => companies.reduce((acc, c) => {
    const u = usage[c.id] || {};
    acc.users += u.users || 0;
    acc.forms += u.forms || 0;
    acc.submissions += u.submissions || 0;
    return acc;
  }, { users: 0, forms: 0, submissions: 0 }), [companies, usage]);

  const activeCompanies = useMemo(
    () => companies.filter((company) => company.isActive !== false).length,
    [companies],
  );

  const filteredCompanies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return companies.filter((company) => {
      const active = company.isActive !== false;
      if (status === 'active' && !active) return false;
      if (status === 'suspended' && active) return false;
      if (!query) return true;
      return [company.name, company.code, company.domain]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [companies, search, status]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.companyName.trim()) return setError('Company name is required.');
    if (!form.adminUsername.trim()) return setError('An admin username is required.');
    if (!form.adminPassword) return setError('An admin password is required.');
    setSaving(true);
    try {
      await companyService.provision({
        companyName: form.companyName.trim(),
        code: form.code.trim() || null,
        color: form.color || null,
        adminUsername: form.adminUsername.trim(),
        adminPassword: form.adminPassword,
        adminDisplayName: form.adminDisplayName.trim() || form.adminUsername.trim(),
        adminEmail: form.adminEmail.trim() || null,
        notes: form.notes.trim() || null,
        requireTwoFactorSetup: true,
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
      await showAlert(
        'The company and its administrator are ready. Share the sign-in details with them securely — the password is not shown again.',
        'Company created',
      );
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create the company.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (company) => {
    const suspending = company.isActive !== false;
    const ok = await showConfirm(
      suspending
        ? `Suspend "${company.name}"? Its users will be signed out and unable to log in. No data is deleted.`
        : `Reactivate "${company.name}"? Its users will be able to log in again.`,
      { title: suspending ? 'Suspend company' : 'Reactivate company', danger: suspending },
    );
    if (!ok) return;

    try {
      if (suspending) await companyService.suspend(company.id);
      else await companyService.activate(company.id);
      await load();
    } catch (err) {
      await showAlert(err?.response?.data?.error || 'The change could not be saved.', 'Failed');
    }
  };

  const deleteCompany = async (company) => {
    const typedName = await showPrompt(
      `This permanently deletes ${company.name}, including its user accounts, company-only templates, form links, submissions, and private form files. Templates still assigned to another company will be kept. This cannot be undone. Type the company name to confirm.`,
      '',
      { title: 'Permanently delete company', okText: 'Delete permanently', placeholder: company.name },
    );
    if (typedName !== company.name) {
      if (typedName !== null) await showAlert('The company name did not match. Nothing was deleted.', 'Deletion cancelled');
      return;
    }

    setDeletingCompanyId(company.id);
    try {
      const result = await companyService.deletePermanently(company.id);
      await load();
      const preserved = result?.preservedSharedTemplates || 0;
      await showAlert(
        `Company deleted. Removed ${result?.deletedUsers || 0} user accounts, ${result?.deletedTemplates || 0} templates, ${result?.deletedLinks || 0} form links, and ${result?.deletedSubmissions || 0} submissions.${preserved ? ` Kept ${preserved} template(s) still assigned to other companies.` : ''}`,
        'Company deleted',
      );
    } catch (err) {
      await showAlert(err?.response?.data?.error || 'The company could not be deleted.', 'Deletion failed');
    } finally {
      setDeletingCompanyId('');
    }
  };

  // Companies sign in on the same scheme the platform itself is served on. Dev runs
  // on plain http, so hard-coding https:// produced a link that refused to connect
  // (TLS on port 80) every time the address was copied from a local workspace.
  const companySignInUrl = (domain) => `${window.location.protocol}//${domain}/`;

  const copySignInAddress = async (company) => {
    if (!company.domain) return;
    const url = companySignInUrl(company.domain);
    // navigator.clipboard is undefined on plain http, which is how the portal is
    // reached on a LAN. copyTextToClipboard falls back to execCommand and reports
    // honestly, so this only shows the address when the copy really failed.
    if (await copyTextToClipboard(url)) {
      await showAlert(`${url} has been copied.`, 'Sign-in address copied');
      return;
    }
    await showAlert(url, 'Company sign-in address');
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch { return '—'; }
  };

  return (
    <div style={styles.page}>
      <div style={styles.head}>
        <div>
          <h1 style={styles.title}>Companies</h1>
          <p style={styles.sub}>
            Provision tenants, manage access and open support tools from one place.
          </p>
        </div>
        <button style={styles.primaryBtn} onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '+ New company'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.metrics}>
        {[
          ['Companies', companies.length],
          ['Active', activeCompanies],
          ['Users', totals.users],
          ['Submissions', totals.submissions],
        ].map(([label, value]) => (
          <div key={label} style={styles.metric}>
            <div style={styles.metricValue}>{value}</div>
            <div style={styles.metricLabel}>{label}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ ...styles.card, padding: 20, marginBottom: 20 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 650, color: '#111827' }}>
            New company
          </h2>
          <p style={{ ...styles.hint, margin: '0 0 18px' }}>
            Creates the company and its first administrator together. The company is given its own
            sign-in address from its code, and its users sign in there — that is what lets two
            companies each have an <code>admin</code>. The administrator sets up two-factor
            authentication on first sign-in.
          </p>

          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Company name *</label>
              <input style={styles.input} value={form.companyName} onChange={setField('companyName')} placeholder="Acme Legal LLP" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Code</label>
              <input style={styles.input} value={form.code} onChange={setField('code')} placeholder="ACME (generated if blank)" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Brand colour</label>
              <input style={{ ...styles.input, padding: 4, height: 38 }} type="color" value={form.color} onChange={setField('color')} />
            </div>
          </div>

          <h3 style={{ margin: '22px 0 4px', fontSize: 14, fontWeight: 650, color: '#111827' }}>
            Company administrator
          </h3>
          <p style={{ ...styles.hint, margin: '0 0 14px' }}>
            Usernames are unique across the whole platform. Using the person's email address avoids
            collisions with other companies.
          </p>

          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Username *</label>
              <input style={styles.input} value={form.adminUsername} onChange={setField('adminUsername')} placeholder="admin@acme.com" autoComplete="off" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Display name</label>
              <input style={styles.input} value={form.adminDisplayName} onChange={setField('adminDisplayName')} placeholder="Acme Administrator" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input style={styles.input} value={form.adminEmail} onChange={setField('adminEmail')} placeholder="admin@acme.com" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Temporary password *</label>
              <input style={styles.input} type="text" value={form.adminPassword} onChange={setField('adminPassword')} autoComplete="new-password" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="submit" style={styles.primaryBtn} disabled={saving}>
              {saving ? 'Creating…' : 'Create company'}
            </button>
            <button type="button" style={styles.ghostBtn} onClick={() => { setShowForm(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={styles.directory}>
        <div style={styles.directoryHead}>
          <div>
            <h2 style={styles.directoryTitle}>Company directory</h2>
            <p style={styles.directoryHint}>Open a company workspace to review templates, links and submissions.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            aria-label="Search companies"
            style={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by company, code or domain"
          />
          <select
            aria-label="Filter company status"
            style={styles.select}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          </div>
        </div>
        {loading && <div style={{ ...styles.card, padding: 36, color: '#6b7280', textAlign: 'center' }}>Loading companies…</div>}
        {!loading && filteredCompanies.length === 0 && (
          <div style={{ ...styles.card, padding: 36, color: '#6b7280', textAlign: 'center' }}>
            {companies.length === 0 ? 'No companies yet. Create the first one to get started.' : 'No companies match this search.'}
          </div>
        )}
        {!loading && filteredCompanies.length > 0 && (
          <div style={styles.companyGrid}>
            {filteredCompanies.map((c) => {
              const u = usage[c.id] || {};
              const active = c.isActive !== false;
              const initials = (c.name || c.code || '?').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
              return (
                <section key={c.id} style={styles.companyCard}>
                  <div style={styles.companyTop}>
                    <div style={styles.companyIdentity}>
                      <div style={{ ...styles.companyAvatar, background: c.primaryColor || c.color || '#273947' }}>{initials}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={styles.companyName}>{c.name}</div>
                        <div style={styles.companyMeta}>{c.code || 'No company code'} · Created {fmtDate(c.createdAt)}</div>
                        {c.domain ? (
                          <a href={companySignInUrl(c.domain)} target="_blank" rel="noreferrer" style={styles.domainLink}>{c.domain} ↗</a>
                        ) : <div style={{ ...styles.companyMeta, color: '#b45309' }}>Sign-in address not configured</div>}
                      </div>
                    </div>
                    <span style={active ? styles.pill('#dcfce7', '#166534') : styles.pill('#fee2e2', '#991b1b')}>
                      {active ? 'Active' : 'Suspended'}
                    </span>
                  </div>

                  <div style={styles.companyStats}>
                    {[
                      ['Users', u.users ?? 0],
                      ['Templates', u.forms ?? 0],
                      ['Submissions', u.submissions ?? 0],
                    ].map(([label, value], index) => (
                      <div key={label} style={{ ...styles.companyStat, ...(index === 2 ? { borderRight: 'none' } : {}) }}>
                        <div style={styles.companyStatValue}>{value}</div>
                        <div style={styles.companyStatLabel}>{label}</div>
                      </div>
                    ))}
                  </div>

                  <div style={styles.companyFooter}>
                    <button type="button" style={styles.openBtn} onClick={() => navigate(`/admin/companies/${encodeURIComponent(c.id)}`)}>
                      Open workspace →
                    </button>
                    <div style={styles.secondaryActions}>
                      <button type="button" style={styles.smallBtn} onClick={() => navigate(`/admin/company?company=${encodeURIComponent(c.id)}`)}>Profile</button>
                      <button type="button" style={styles.smallBtn} onClick={() => navigate(`/admin/users?company=${encodeURIComponent(c.id)}`)}>Users</button>
                      {c.domain && <button type="button" style={styles.smallBtn} onClick={() => copySignInAddress(c)}>Copy URL</button>}
                      <button
                        type="button"
                        style={active ? styles.suspendActionBtn : styles.reactivateBtn}
                        onClick={() => toggleActive(c)}
                        aria-label={`${active ? 'Suspend' : 'Reactivate'} ${c.name}`}
                        title={active ? 'Suspend this company' : 'Reactivate this company'}
                      >
                        {active ? (
                          <>
                            <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M8 1.75a6.25 6.25 0 1 0 0 12.5a6.25 6.25 0 0 0 0-12.5ZM6.25 5.5v5M9.75 5.5v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                            </svg>
                            Suspend
                          </>
                        ) : (
                          <>
                            <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M13 6a5.25 5.25 0 1 0 .25 3M13 2.75V6h-3.25" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Reactivate
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        style={styles.deleteBtn}
                        onClick={() => deleteCompany(c)}
                        disabled={deletingCompanyId === c.id}
                        aria-label={`Permanently delete ${c.name}`}
                        title="Permanently delete this company"
                      >
                        <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M2.5 4h11M6 2.5h4M4 4l.6 9h6.8l.6-9M6.5 6.5v4M9.5 6.5v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {deletingCompanyId === c.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
